import { FunctionsHttpError, type Session } from '@supabase/supabase-js';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import {
  supabaseFunctionWithRetry,
  supabaseRpcWithRetry,
  supabaseQueryWithRetry,
} from '@/lib/networkRetry';
import { createLogger } from '@/lib/structuredLogger';
import {
  Profile,
  ProfileCompanyInfo,
  ProfileCompanyEmployeeData,
  ProfileCompanyData,
  FitbitStatus,
  ProfileData,
} from '@/types/profile';

const logger = createLogger('ProfileData');

// Re-export types for backward compatibility
export type {
  Profile,
  ProfileCompanyInfo as CompanyInfo,
  ProfileCompanyEmployeeData as CompanyEmployeeData,
  ProfileCompanyData as CompanyData,
  FitbitStatus,
  ProfileData,
};

/**
 * Fetch all profile data using batched RPC
 *
 * OPTIMIZED: Single query instead of 3 separate queries
 * Performance: 300ms → 100ms (66% reduction)
 *
 * @param userId - User ID to fetch data for
 * @param session - Supabase session (for auth)
 * @returns Combined profile data
 */
export async function fetchProfileData(userId: string, session: Session): Promise<ProfileData> {
  try {
    // Use batched RPC for single round-trip
    const { data, error } = await supabaseRpcWithRetry<ProfileData>(supabase, 'get_profile_data', {
      user_id_param: userId,
    });

    if (error) {
      logger.error(
        'Batched profile query error',
        error instanceof Error ? error : new Error(String(error)),
        { userId },
      );
      // Fallback to parallel queries
      return fetchProfileDataFallback(userId, session);
    }
    if (!data) {
      return fetchProfileDataFallback(userId, session);
    }

    return {
      profile: data.profile || null,
      company: data.company || null,
      fitbit_status: data.fitbit_status || { connected: false },
      roles: data.roles || [],
    };
  } catch (err) {
    logger.error(
      'Batched profile query exception',
      err instanceof Error ? err : new Error(String(err)),
      { userId },
    );
    // Fallback to parallel queries
    return fetchProfileDataFallback(userId, session);
  }
}

/**
 * Fallback: Fetch all profile data in parallel (old method)
 * Used if batched RPC fails
 */
async function fetchProfileDataFallback(userId: string, _session: Session): Promise<ProfileData> {
  logger.warn('Using fallback parallel queries for profile', { userId });

  try {
    // Get profile
    const { data: profileData, error: profileError } = await supabaseQueryWithRetry(() =>
      supabase.from('profiles').select('*').eq('id', userId).maybeSingle(),
    );

    if (profileError) {
      logger.error(
        'Profile fetch failed in fallback',
        profileError instanceof Error ? profileError : new Error(String(profileError)),
        { userId },
      );
    }

    // Get company
    const { data: companyData, error: companyError } = await supabaseQueryWithRetry(() =>
      supabase
        .from('company_employees')
        .select(
          `
          company_id,
          companies!inner(
            id,
            name,
            display_name
          )
        `,
        )
        .eq('user_id', userId)
        .eq('is_active', true)
        .maybeSingle(),
    );

    if (companyError) {
      logger.warn('Company fetch failed in fallback', {
        error: companyError instanceof Error ? companyError.message : String(companyError),
        userId,
      });
    }

    // Get Fitbit status
    const { data: fitbitData, error: fitbitError } = await supabaseRpcWithRetry<{
      connected?: boolean;
      needs_relogin?: boolean;
    }>(supabase, 'get_fitbit_connection_status', { p_user_id: userId });

    if (fitbitError && fitbitError.code !== 'PGRST116') {
      logger.warn('Fitbit status fetch failed in fallback', {
        error: fitbitError instanceof Error ? fitbitError.message : String(fitbitError),
        userId,
      });
    }

    // Check if Fitbit is connected
    let fitbitConnected = false;
    fitbitConnected = Boolean(fitbitData?.connected && !fitbitData.needs_relogin);

    const typedCompanyData = companyData as ProfileCompanyEmployeeData | null;

    return {
      profile: profileData || null,
      company: typedCompanyData
        ? {
            company_id: typedCompanyData.company_id || null,
            company: typedCompanyData.companies
              ? {
                  id: typedCompanyData.companies.id,
                  name: typedCompanyData.companies.name,
                  display_name: typedCompanyData.companies.display_name,
                }
              : null,
          }
        : null,
      fitbit_status: { connected: fitbitConnected },
    };
  } catch (err) {
    logger.error(
      'Fallback profile fetch exception',
      err instanceof Error ? err : new Error(String(err)),
      { userId },
    );
    // Return empty data on complete failure
    return {
      profile: null,
      company: null,
      fitbit_status: { connected: false },
    };
  }
}

export type ConsentWithdrawalResult =
  { status: 'complete' } | { status: 'provider_cleanup_pending' };

const withdrawalCompleteReceipt = z.object({
  success: z.literal(true),
  mode: z.literal('withdraw'),
});

const withdrawalPendingReceipt = z.object({
  success: z.literal(false),
  mode: z.literal('withdraw'),
  code: z.literal('provider_cleanup_pending'),
  local_cleanup_complete: z.literal(true),
  retryable: z.literal(true),
});

export async function withdrawDataConsent(userId: string): Promise<ConsentWithdrawalResult> {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  const session = sessionData.session;
  if (sessionError || session?.user.id !== userId || !session.access_token) {
    throw new Error('Consent withdrawal requires the initiating account session');
  }

  // The server derives ownership from this bearer. Never replay a destructive withdrawal
  // with an automatically refreshed/replacement account's session.
  const { data, error } = await supabaseFunctionWithRetry(
    supabase,
    'revoke-health-data-processing',
    {
      body: { mode: 'withdraw' },
      headers: { Authorization: `Bearer ${session.access_token}` },
    },
    { maxRetries: 0 },
  );

  if (!error) {
    if (!withdrawalCompleteReceipt.safeParse(data).success) {
      throw new Error('Consent withdrawal returned an invalid completion receipt');
    }
    return { status: 'complete' };
  }

  if (
    error instanceof FunctionsHttpError &&
    error.context instanceof Response &&
    error.context.status === 502
  ) {
    // A partial receipt is authoritative only for this exact server contract. Preserve
    // the original error response for callers; unknown/malformed failures remain errors.
    const receipt = await error.context
      .clone()
      .json()
      .catch(() => null);
    if (withdrawalPendingReceipt.safeParse(receipt).success) {
      return { status: 'provider_cleanup_pending' };
    }
  }
  throw error;
}

export type WearableDisconnectProvider =
  | 'fitbit'
  | 'google_health'
  | 'open_wearables'
  | 'wearable'
  | (string & {});

function normalizeWearableDisconnectProvider(provider: string | null | undefined): string {
  const normalized = provider?.trim().toLowerCase().replace(/[\s-]+/g, '_');

  switch (normalized) {
    case undefined:
    case '':
      return 'wearable';
    case 'fitbit':
      return 'fitbit';
    case 'google':
    case 'google_health':
    case 'google_health_connect':
    case 'health_connect':
      return 'google_health';
    case 'openwearables':
    case 'open_wearables':
      return 'open_wearables';
    case 'wearable':
      return 'wearable';
    default:
      if (/^[a-z0-9_]+$/.test(normalized)) {
        return normalized;
      }
      throw new Error('Unsupported wearable provider');
  }
}

export async function disconnectWearableIntegration({
  userId,
  provider,
}: {
  userId: string;
  provider?: string | null;
}): Promise<void> {
  const normalizedProvider = normalizeWearableDisconnectProvider(provider);

  logger.info('Disconnecting wearable integration', {
    userId,
    provider: normalizedProvider,
  });

  const { error } = await supabaseFunctionWithRetry(supabase, 'revoke-health-data-processing', {
    body: {
      mode: 'disconnect_wearable',
      provider: normalizedProvider,
      user_id: userId,
    },
  });

  if (error) {
    logger.error('Failed to disconnect wearable integration', error, {
      userId,
      provider: normalizedProvider,
    });
    throw error;
  }
}
